import React, { useState, useCallback } from 'react'
import { apis } from '@modules/apiClients'
import { useTar } from '@/modules'

interface MigrateTaskActionProps {
  taskId: string
  taskName: string
  currentSharedDBName?: string
}

export const MigrateTaskAction: React.FC<MigrateTaskActionProps> = ({
  taskId,
  taskName,
  currentSharedDBName,
}) => {
  const { t } = useTar()
  const [sharedDBName, setSharedDBName] = useState('')
  const [isRunning, setIsRunning] = useState(false)
  const [result, setResult] = useState<{
    success: boolean
    steps: { step: string; status: string; error?: string }[]
  } | null>(null)
  const [error, setError] = useState<string | null>(null)

  const handleMigrate = useCallback(async () => {
    if (!sharedDBName.trim()) return
    setError(null)
    setResult(null)
    setIsRunning(true)
    try {
      const response = await apis.taskService.adminActionsMigrateTaskToSharedDB({
        taskId,
        sharedDBName: sharedDBName.trim(),
      })
      setResult(response.data)
    } catch (err: unknown) {
      const errorMessage = (err as { response?: { data?: { error?: string } } })?.response?.data?.error || t('adminActions.migrateTaskFailed')
      setError(errorMessage)
    } finally {
      setIsRunning(false)
    }
  }, [taskId, sharedDBName, t])

  if (currentSharedDBName) {
    return (
      <div className="space-y-2 border-t border-border pt-4">
        <h4 className="text-sm font-medium text-foreground">{t('adminActions.migrateToSharedDB')}</h4>
        <p className="text-xs text-muted-foreground">
          {t('adminActions.alreadyUsesSharedDB', { sharedDBName: currentSharedDBName })}
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-3 border-t border-border pt-4">
      <h4 className="text-sm font-medium text-foreground">{t('adminActions.migrateToSharedDB')}</h4>
      <p className="text-xs text-muted-foreground">
        {t('adminActions.migrateDescription', { taskName })}
      </p>

      <div className="flex gap-2 items-end">
        <div className="flex-1 space-y-1">
          <label className="text-xs font-medium text-muted-foreground">{t('adminActions.newSharedDBName')}</label>
          <input
            type="text"
            value={sharedDBName}
            onChange={(e) => setSharedDBName(e.target.value)}
            placeholder={t('adminActions.sharedDBNamePlaceholder')}
            disabled={isRunning}
            className="w-full px-3 py-1.5 text-sm border border-border rounded-md bg-background text-foreground placeholder:text-muted-foreground/50 disabled:opacity-50"
          />
        </div>
        <button
          onClick={handleMigrate}
          disabled={isRunning || !sharedDBName.trim()}
          className="px-4 py-1.5 text-sm font-medium text-white bg-accent rounded-md hover:bg-accent/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
        >
          {isRunning ? t('adminActions.migrating') : t('adminActions.migrate')}
        </button>
      </div>

      {error && (
        <div className="p-2 bg-red-500/10 border border-red-500/20 rounded-md">
          <p className="text-xs text-red-500">{error}</p>
        </div>
      )}

      {result && (
        <div className={`p-3 rounded-md border ${result.success ? 'bg-green-500/10 border-green-500/20' : 'bg-red-500/10 border-red-500/20'}`}>
          <p className={`text-sm font-medium ${result.success ? 'text-green-600' : 'text-red-500'}`}>
            {result.success ? t('adminActions.migrationCompleted') : t('adminActions.migrationFailed')}
          </p>
          <div className="mt-2 space-y-1">
            {result.steps.map((step, i) => (
              <div key={i} className="flex items-center gap-2 text-xs">
                <span className={step.status === 'success' ? 'text-green-600' : 'text-red-500'}>
                  {step.status === 'success' ? '✓' : '✗'}
                </span>
                <span className="text-foreground">{step.step}</span>
                {step.error && <span className="text-red-500">— {step.error}</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
